import { ReactNode, Ref, useId, useState } from "react";
import {
  autoUpdate,
  ElementProps,
  flip,
  FloatingFocusManager,
  offset,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import usePopoverAria from "./usePopoverAria.ts";

interface TriggerProps extends ElementProps {
  ref: Ref<HTMLButtonElement>;
  "data-return-focus-destination": string;
}

interface PopoverProps {
  children: ReactNode;
  renderTrigger: (props: TriggerProps) => ReactNode;
  containerClass?: string;
  xOffset?: number;
  yOffset?: number;
  placement: Placement;
}

export default function PopoverDisclosure({
  children,
  renderTrigger,
  placement,
  xOffset = 0,
  yOffset = 0,
}: PopoverProps) {
  const popoverId = useId();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isPopoverOpen,
    onOpenChange: setIsPopoverOpen,
    placement,
    middleware: [
      offset({ mainAxis: yOffset, crossAxis: xOffset }),
      flip({
        mainAxis: true,
      }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const aria = usePopoverAria(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    aria,
  ]);

  return (
    <>
      {renderTrigger({
        ref: refs.setReference,
        "data-return-focus-destination": popoverId,
        ...getReferenceProps(),
      })}
      {isPopoverOpen && (
        <FloatingFocusManager
          context={context}
          closeOnFocusOut={true}
          returnFocus={false}
          guards={false}
          modal={false}
          visuallyHiddenDismiss={false}
          initialFocus={-1}
        >
          {/* This is ok because it's capturing general interaction inside the popover,
               not providing a button-style interaction */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            data-return-focus-source={popoverId}
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 999,
            }}
            onClick={() => {
              setIsPopoverOpen(false);
              const ref = refs.reference?.current as
                | HTMLButtonElement
                | undefined;
              ref?.focus();
            }}
            {...getFloatingProps()}
          >
            {isPopoverOpen ? children : null}
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
}
